"use client";

import { useState, useMemo, Suspense } from "react";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { IMAGES_PER_PAGE } from "@configs/gallery";
import type { GalleryProps } from "@interfaces/gallery";
import { LoadMoreButton } from "@components/atoms/LoadMoreButton.tsx/LoadMoreButton";
import { NavigationTab } from "@components/molecules/NavigationTab/NavigationTab";
import { SubTab } from "@components/molecules/SubTab/SubTab";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import {
  filterImagesByCategory,
  getDisplayedImages,
  hasMoreMainImages,
  getImageCount,
  getAvailableSubTabs,
  getAvailableNavigationTabs,
} from "@utils/gallery-utils";
import styles from "./Gallery.module.scss";
import { PAGE_TYPES } from "@constants/pageTypes";
import { table } from "console";

const MobileGrid = dynamic(
  () =>
    import("@components/organisms/MobileGrid/MobileGrid").then(
      (mod) => mod.MobileGrid
    ),

  {
    loading: () => <div className={styles["gallery__loading"]} />,
  }
);
const DesktopGrid = dynamic(
  () =>
    import("@components/organisms/DesktopGrid/DesktopGrid").then(
      (mod) => mod.DesktopGrid
    ),

  {
    loading: () => <div className={styles["gallery__loading"]} />,
  }
);

export const Gallery = ({
  images,
  modelName,
  initialVariantsCount,
  slug,
  cityName,
  makeName,
  carAvailability,
}: GalleryProps) => {
  const router = useRouter();

  // Get available navigation tabs
  const availableNavigationTabs = useMemo(
    () => getAvailableNavigationTabs(images),
    [images]
  );

  const [activeTab, setActiveTab] = useState<string>(
    availableNavigationTabs.length > 0
      ? availableNavigationTabs[0].id
      : "exterior"
  );
  const [activeSubTab, setActiveSubTab] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const handleImageClick = async (variantId: string) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.GALLERY_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_image",
        city_name: cityName,
        vehicle_make: makeName,
        vehicle_model: modelName,
        car_availability: carAvailability,
      },
    });
    const url = `/cars/${slug}/images/${activeTab}?current=${variantId}`;
    router.push(url);
  };

  const handleSubTabClick = async (tab: any) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.GALLERY_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter",
        city_name: cityName,
        vehicle_make: makeName,
        vehicle_model: modelName,
        car_availability: carAvailability,
        selected_value: tab.id,
      },
    });
    setActiveSubTab(activeSubTab === tab.id ? null : tab.id);
  };

  const filteredImages = useMemo(
    () => filterImagesByCategory(images, activeTab, activeSubTab),
    [images, activeTab, activeSubTab]
  );
  const displayedImages = useMemo(
    () => getDisplayedImages(filteredImages, page, IMAGES_PER_PAGE),
    [filteredImages, page]
  );
  const hasMore = useMemo(
    () => hasMoreMainImages(displayedImages, filteredImages),
    [displayedImages, filteredImages]
  );

  // Get dynamic sub-tabs based on available images for the active tab
  const availableSubTabs = useMemo(
    () => getAvailableSubTabs(images, activeTab),
    [images, activeTab]
  );

  // Reset active sub-tab when active tab changes and the current sub-tab is not available
  useMemo(() => {
    if (
      activeSubTab &&
      !availableSubTabs.find((tab) => tab.id === activeSubTab)
    ) {
      setActiveSubTab(null);
    }
  }, [activeTab, availableSubTabs, activeSubTab]);
  return (
    <div className={styles.gallery}>
      <div className={styles["gallery__container"]}>
        <h1 className={styles["gallery__title"]}> {`${modelName} images and videos`}</h1>

        {/* Main Navigation */}

        <div className={styles["gallery__nav"]}>
          <div className={styles["gallery__nav-tabs"]}>
            {availableNavigationTabs.map((tab) => (
              <NavigationTab
                key={tab.id}
                id={tab.id}
                label={tab.label}
                isActive={activeTab === tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setPage(1);
                }}
              />
            ))}
          </div>
        </div>

        {availableSubTabs.length > 0 && (
          <div className={styles["gallery__sub-tabs"]}>
            <div className={styles["gallery__sub-tabs-container"]}>
              {availableSubTabs.map((tab) => (
                <SubTab
                  key={tab.id}
                  id={tab.id}
                  label={tab.label}
                  isActive={activeSubTab === tab.id}
                  onClick={() =>
                    handleSubTabClick(tab)
                  }
                />
              ))}
            </div>
          </div>
        )}

        {activeSubTab && (
          <div className={styles["gallery__sub-heading"]}>
            {availableSubTabs.find((t) => t.id === activeSubTab)?.label}{" "}
            images ({getImageCount(images, activeSubTab, activeTab)})
          </div>
        )}

        {/* Mobile Grid */}

        <div className={styles["gallery__grid--mobile"]}>
          <Suspense fallback={<div className={styles["gallery__loading"]} />}>
            <MobileGrid
              displayedImages={displayedImages}
              handleImageClick={handleImageClick}
              initialVariantsCount={initialVariantsCount}
            />
          </Suspense>
        </div>

        {/* Desktop Grid */}

        <div className={styles["gallery__grid--desktop"]}>
          <Suspense fallback={<div className={styles["gallery__loading"]} />}>
            <DesktopGrid
              displayedImages={displayedImages}
              handleImageClick={handleImageClick}
              initialVariantsCount={initialVariantsCount}
            />
          </Suspense>
        </div>

        {hasMore && (
          <LoadMoreButton onClick={() => setPage((prev) => prev + 1)} />
        )}
      </div>
    </div>
  );
};
